/**
 * ATTemplatesBar — Barre d'actions Modèles favoris pour la création d'AT.
 * - Charger un modèle en 1 clic → crée une nouvelle AT et redirige vers son édition
 * - Sauvegarder l'AT en cours comme modèle réutilisable
 *
 * Affiché uniquement quand type === 'autorisation_travail'.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark, BookmarkPlus, Trash2, ChevronDown, Sparkles } from 'lucide-react';
import { apiATTemplates } from '@/lib/api';
import { toast } from 'sonner';

export default function ATTemplatesBar({ currentAtId, currentAtTitre, onTemplateLoaded }) {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const reload = async () => {
    try {
      const { data } = await apiATTemplates.list();
      setTemplates(data || []);
    } catch (e) {
      console.error('Erreur chargement modèles:', e);
    }
  };

  useEffect(() => { reload(); }, []);

  const handleInstantiate = async (tpl) => {
    setLoading(true);
    setOpen(false);
    try {
      const { data } = await apiATTemplates.instantiate(tpl.id);
      toast.success(`AT ${data.numero} créée depuis « ${tpl.name} »`);
      if (onTemplateLoaded) onTemplateLoaded(data.id);
      navigate(`/permits/new?edit=${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!currentAtId) {
      toast.error("Sauvegardez d'abord l'AT avant de la transformer en modèle");
      return;
    }
    const name = window.prompt(
      'Nom du modèle (ex: "Maintenance broyeur ligne 1 — mensuelle") :',
      currentAtTitre || ''
    );
    if (!name || !name.trim()) return;
    setSaving(true);
    try {
      await apiATTemplates.create({
        name: name.trim(),
        source_permit_id: currentAtId,
        shared: false,
      });
      toast.success(`Modèle « ${name} » enregistré`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  const handleDelete = async (tpl, e) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer le modèle « ${tpl.name} » ?`)) return;
    try {
      await apiATTemplates.remove(tpl.id);
      toast.success('Modèle supprimé');
      reload();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="at-templates-bar">
      {/* Charger un modèle */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          disabled={loading || templates.length === 0}
          data-testid="at-templates-open-btn"
          className="inline-flex items-center gap-2 px-3 py-2 bg-[#09090B] hover:bg-stone-700 disabled:bg-stone-400 transition-colors text-white text-xs font-bold uppercase tracking-wider"
        >
          <Sparkles className="w-4 h-4" />
          {templates.length === 0 ? 'Aucun modèle' : `Charger un modèle (${templates.length})`}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && templates.length > 0 && (
          <div
            data-testid="at-templates-dropdown"
            className="absolute z-30 top-full mt-1 left-0 bg-white border-2 border-[#09090B] shadow-xl w-[420px] max-h-96 overflow-auto"
          >
            {templates.map((tpl) => (
              <div
                key={tpl.id}
                onClick={() => handleInstantiate(tpl)}
                className="group flex items-center justify-between gap-2 px-3 py-2 border-b border-stone-200 hover:bg-blue-50 cursor-pointer"
                data-testid={`at-template-row-${tpl.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-[#09090B] truncate">{tpl.name}</div>
                  <div className="text-[10px] text-stone-500 truncate uppercase tracking-wider mt-0.5">
                    {tpl.site_code && <span className="font-mono">{tpl.site_code}</span>}
                    {tpl.snapshot?.entreprise_exterieure && <span> · {tpl.snapshot.entreprise_exterieure}</span>}
                    {tpl.usage_count > 0 && <span> · utilisé {tpl.usage_count}×</span>}
                  </div>
                  {tpl.snapshot?.lieu && (
                    <div className="text-[10px] text-stone-600 truncate mt-0.5 italic">{tpl.snapshot.lieu}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => handleDelete(tpl, e)}
                  data-testid={`at-template-delete-${tpl.id}`}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-stone-400 hover:text-red-600"
                  title="Supprimer ce modèle"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sauvegarder comme modèle */}
      <button
        type="button"
        onClick={handleSave}
        disabled={saving || !currentAtId}
        data-testid="at-template-save-btn"
        title={!currentAtId ? "Sauvegardez d'abord l'AT" : 'Sauvegarder cette AT comme modèle'}
        className="inline-flex items-center gap-2 px-3 py-2 bg-white border-2 border-[#09090B] hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-[#09090B] text-xs font-bold uppercase tracking-wider"
      >
        <BookmarkPlus className="w-4 h-4" />
        {saving ? 'Sauvegarde...' : 'Sauver comme modèle'}
      </button>

      <div className="text-[10px] text-stone-500 italic uppercase tracking-wider flex items-center gap-1">
        <Bookmark className="w-3 h-3" />
        Réutilise une AT existante en 1 clic
      </div>
    </div>
  );
}
